/**
 * Stock Movements Page - Track all stock entries and exits
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { useInstance } from '../../../contexts/InstanceContext';

import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { DataGrid } from '../../../components/ui/DataGrid';
import PageHeader from '../../../components/ui/PageHeader';
import { Modal } from '../../../components/ui/modal';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Textarea } from '../../../components/ui/textarea';
import { Plus, ArrowUpCircle, ArrowDownCircle, RefreshCw, TrendingUp, TrendingDown, Eye } from 'lucide-react';

import './styles.css';

const MOVEMENT_TYPES = [
  { value: 'entry', label: 'Entrada', icon: ArrowUpCircle, color: 'text-emerald-500' },
  { value: 'exit', label: 'Saída', icon: ArrowDownCircle, color: 'text-red-500' },
  { value: 'adjustment', label: 'Ajuste', icon: RefreshCw, color: 'text-blue-500' },
];

const MOVEMENT_REASONS = {
  entry: [
    { value: 'purchase', label: 'Compra' },
    { value: 'return', label: 'Devolução' },
    { value: 'production', label: 'Produção' },
    { value: 'adjustment', label: 'Ajuste de Inventário' },
  ],
  exit: [
    { value: 'sale', label: 'Venda' },
    { value: 'loss', label: 'Perda/Avaria' },
    { value: 'expired', label: 'Vencido' },
    { value: 'transfer', label: 'Transferência' },
    { value: 'adjustment', label: 'Ajuste de Inventário' },
  ],
  adjustment: [
    { value: 'adjustment', label: 'Ajuste de Inventário' },
  ],
};

export default function Movements() {
  const { t } = useTranslation();
  const { formatCurrency, formatDate } = useInstance();
  
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filters, setFilters] = useState({});
  
  const [modalOpen, setModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  
  const [formData, setFormData] = useState({
    product_id: '',
    movement_type: 'entry',
    reason: 'purchase',
    quantity: '',
    unit_cost: '',
    notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load products for filter dropdown
      const productsRes = await api.get('/products?active_only=false');
      setProducts(productsRes.data);
      
      // Load movements with pagination
      const movementsRes = await api.post('/stock-movements/list', {
        pageNumber,
        pageSize,
        sortBy,
        sortDirection,
        filters: {
          product_id: filters.product_id || null,
          movement_type: filters.movement_type || null,
          reason: filters.reason || null,
          start_date: filters.start_date || null,
          end_date: filters.end_date || null,
        },
      });
      
      if (movementsRes.data.success) {
        setMovements(movementsRes.data.data.items);
        setTotalItems(movementsRes.data.data.totalItems);
      }
    } catch (err) {
      toast.error('Erro ao carregar movimentações');
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, sortBy, sortDirection, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        product_id: formData.product_id,
        movement_type: formData.movement_type,
        reason: formData.reason,
        quantity: parseInt(formData.quantity) || 0,
        unit_cost: formData.unit_cost ? parseFloat(formData.unit_cost) : null,
        notes: formData.notes || null,
      };
      
      await api.post('/stock-movements', data);
      toast.success('Movimentação registrada com sucesso!');
      
      setModalOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao registrar movimentação');
    }
  };

  // Handle modal close - reset form when closing
  const handleModalOpenChange = (open) => {
    setModalOpen(open);
    if (!open) {
      setTimeout(() => resetForm(), 100);
    }
  };

  const resetForm = () => {
    setFormData({
      product_id: '',
      movement_type: 'entry',
      reason: 'purchase',
      quantity: '',
      unit_cost: '',
      notes: '',
    });
  };

  const handleViewDetails = (movement) => {
    setSelectedMovement(movement);
    setDetailsModalOpen(true);
  };

  const columns = useMemo(() => [
    {
      field: 'created_at',
      headerName: 'Data',
      width: 150,
      valueFormatter: (params) => formatDate(params.value, true),
    },
    {
      field: 'product_name',
      headerName: 'Produto',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'movement_type',
      headerName: 'Tipo',
      width: 100,
      cellRenderer: (params) => {
        const type = MOVEMENT_TYPES.find(t => t.value === params.value);
        if (!type) return params.value;
        const Icon = type.icon;
        return (
          <div className={`flex items-center gap-1 ${type.color}`}>
            <Icon className="h-4 w-4" />
            <span>{type.label}</span>
          </div>
        );
      },
    },
    {
      field: 'reason',
      headerName: 'Motivo',
      width: 130,
      valueFormatter: (params) => {
        const allReasons = [...MOVEMENT_REASONS.entry, ...MOVEMENT_REASONS.exit, ...MOVEMENT_REASONS.adjustment];
        const reason = allReasons.find(r => r.value === params.value);
        return reason?.label || params.value;
      },
    },
    {
      field: 'quantity',
      headerName: 'Qtd',
      width: 80,
      cellRenderer: (params) => (
        <span className={params.value >= 0 ? 'text-emerald-500' : 'text-red-500'}>
          {params.value >= 0 ? '+' : ''}{params.value}
        </span>
      ),
    },
    {
      field: 'previous_stock',
      headerName: 'Estoque Ant.',
      width: 100,
      type: 'number',
    },
    {
      field: 'new_stock',
      headerName: 'Estoque Atual',
      width: 110,
      type: 'number',
    },
    {
      field: 'batch_number',
      headerName: 'Lote',
      width: 100,
      valueFormatter: (params) => params.value || '-',
    },
    {
      field: 'notes',
      headerName: 'Observação',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 70,
      sortable: false,
      cellRenderer: (params) => (
        <Button variant="ghost" size="icon" onClick={() => handleViewDetails(params.data)} title="Ver detalhes">
          <Eye className="h-4 w-4" />
        </Button>
      ),
    },
  ], [formatDate]);

  const filterDefinitions = [
    {
      key: 'product_id',
      label: 'Produto',
      type: 'select',
      placeholder: 'Todos os produtos',
      options: [
        { value: '', label: 'Todos os produtos' },
        ...products.map(p => ({ value: p.id, label: p.name })),
      ],
    },
    {
      key: 'movement_type',
      label: 'Tipo',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: '', label: 'Todos' },
        ...MOVEMENT_TYPES.map(t => ({ value: t.value, label: t.label })),
      ],
    },
    {
      key: 'start_date',
      label: 'Data Início',
      type: 'date',
    },
    {
      key: 'end_date',
      label: 'Data Fim',
      type: 'date',
    },
  ];

  // Summary stats
  const stats = useMemo(() => {
    const entries = movements.filter(m => m.movement_type === 'entry').reduce((sum, m) => sum + m.quantity, 0);
    const exits = movements.filter(m => m.movement_type === 'exit').reduce((sum, m) => sum + Math.abs(m.quantity), 0);
    return { entries, exits };
  }, [movements]);

  return (
    <div className="movements-page" data-testid="movements-page">
      <PageHeader
        icon={RefreshCw}
        title="Movimentação de Estoque"
        subtitle={`${totalItems} movimentações registradas`}
        actions={
          <Button onClick={() => { resetForm(); setModalOpen(true); }} data-testid="new-movement-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nova Movimentação
          </Button>
        }
        filters={filterDefinitions}
        filterValues={filters}
        onFilterChange={(newFilters) => {
          setFilters(newFilters);
          setPageNumber(1);
        }}
        onSearch={() => loadData()}
        onClearFilters={() => {
          setFilters({});
          setPageNumber(1);
        }}
        exportFileName="movimentacoes"
        exportData={movements}
      />

      {/* Summary Cards */}
      <div className="movements-summary">
        <div className="movement-stat entry">
          <TrendingUp className="h-5 w-5" />
          <div>
            <span className="stat-value">+{stats.entries}</span>
            <span className="stat-label">Entradas</span>
          </div>
        </div>
        <div className="movement-stat exit">
          <TrendingDown className="h-5 w-5" />
          <div>
            <span className="stat-value">-{stats.exits}</span>
            <span className="stat-label">Saídas</span>
          </div>
        </div>
      </div>

      <DataGrid
        title="Movimentações de Estoque"
        rowData={movements}
        columnDefs={columns}
        loading={loading}
        pagination={true}
        paginationPageSize={pageSize}
        onPaginationChanged={(params) => {
          const newPage = params.api.paginationGetCurrentPage() + 1;
          if (newPage !== pageNumber) {
            setPageNumber(newPage);
          }
        }}
        onSortChanged={(params) => {
          const sortModel = params.api.getSortModel();
          if (sortModel.length > 0) {
            setSortBy(sortModel[0].colId);
            setSortDirection(sortModel[0].sort);
          }
        }}
      />

      {/* Create Movement Modal */}
      <Modal
        open={modalOpen}
        onOpenChange={handleModalOpenChange}
        title="Nova Movimentação de Estoque"
        description="Registre uma entrada, saída ou ajuste de estoque"
        size="default"
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => handleModalOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" form="movement-form" data-testid="save-movement-btn">Registrar</Button>
          </>
        }
      >
        <form id="movement-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Produto *</Label>
            <Select
              value={formData.product_id}
              onValueChange={(v) => setFormData(p => ({ ...p, product_id: v }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecione um produto" />
              </SelectTrigger>
              <SelectContent>
                {products.filter(p => p.track_stock).map(product => (
                  <SelectItem key={product.id} value={product.id}>
                    {product.name} (Estoque: {product.stock})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Tipo *</Label>
              <Select
                value={formData.movement_type}
                onValueChange={(v) => setFormData(p => ({ 
                  ...p, 
                  movement_type: v,
                  reason: MOVEMENT_REASONS[v][0].value 
                }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOVEMENT_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Motivo *</Label>
              <Select
                value={formData.reason}
                onValueChange={(v) => setFormData(p => ({ ...p, reason: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOVEMENT_REASONS[formData.movement_type].map(reason => (
                    <SelectItem key={reason.value} value={reason.value}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Quantidade *</Label>
              <Input
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData(p => ({ ...p, quantity: e.target.value }))}
                required
                data-testid="movement-quantity-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Custo Unitário</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_cost}
                onChange={(e) => setFormData(p => ({ ...p, unit_cost: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Observações</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
              rows={3}
            />
          </div>
        </form>
      </Modal>

      {/* Details Modal */}
      <Modal
        open={detailsModalOpen}
        onOpenChange={setDetailsModalOpen}
        title={
          <span className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Detalhes da Movimentação
          </span>
        }
        size="default"
        showConfirm={false}
        cancelText="Fechar"
        onCancel={() => setDetailsModalOpen(false)}
      >
        {selectedMovement && (
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="font-semibold text-lg">{selectedMovement.product_name}</h3>
              <p className="text-sm text-muted-foreground">
                {formatDate(selectedMovement.created_at, true)}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Tipo</p>
                {(() => {
                  const type = MOVEMENT_TYPES.find(t => t.value === selectedMovement.movement_type);
                  if (!type) return selectedMovement.movement_type;
                  const Icon = type.icon;
                  return (
                    <div className={`flex items-center gap-2 font-semibold ${type.color}`}>
                      <Icon className="h-5 w-5" />
                      <span>{type.label}</span>
                    </div>
                  );
                })()}
              </div>
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Motivo</p>
                <p className="font-medium">
                  {(() => {
                    const allReasons = [...MOVEMENT_REASONS.entry, ...MOVEMENT_REASONS.exit, ...MOVEMENT_REASONS.adjustment];
                    const reason = allReasons.find(r => r.value === selectedMovement.reason);
                    return reason?.label || selectedMovement.reason;
                  })()}
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-lg">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Estoque Anterior</p>
                <p className="font-mono font-semibold text-lg">{selectedMovement.previous_stock}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Quantidade</p>
                <p className={`font-mono font-semibold text-lg ${selectedMovement.quantity >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {selectedMovement.quantity >= 0 ? '+' : ''}{selectedMovement.quantity}
                </p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Estoque Atual</p>
                <p className="font-mono font-semibold text-lg">{selectedMovement.new_stock}</p>
              </div>
            </div>
            
            {selectedMovement.unit_cost && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Custo Unitário</p>
                  <p className="font-mono font-semibold">{formatCurrency(selectedMovement.unit_cost)}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Custo Total</p>
                  <p className="font-mono font-semibold">
                    {formatCurrency(selectedMovement.unit_cost * Math.abs(selectedMovement.quantity))}
                  </p>
                </div>
              </div>
            )}
            
            {selectedMovement.batch_number && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Lote</p>
                <Badge variant="outline">{selectedMovement.batch_number}</Badge>
              </div>
            )}
            
            {selectedMovement.notes && (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Observações</p>
                <p className="text-sm bg-muted p-2 rounded">{selectedMovement.notes}</p>
              </div>
            )}
            
            {selectedMovement.user_name && (
              <div className="text-xs text-muted-foreground pt-2 border-t">
                <p>Registrado por: {selectedMovement.user_name}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
